/**
 * Feature Flags Utility
 * 
 * This utility provides a centralized way to check environment variables
 * for feature flags. All flags default to hidden (false) if not explicitly
 * set to 'true' in the environment.
 */

/**
 * Check if a feature flag is enabled
 * @param flagName - The environment variable name (without VITE_ prefix)
 * @returns true if the flag is set to a truthy string (case-insensitive)
 */
const isFeatureEnabled = (flagName: string): boolean => {
  const envVarName = `VITE_${flagName}`;
  const value = import.meta.env[envVarName as keyof ImportMetaEnv];
  if (value === undefined || value === null) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

/**
 * Settings Feature Flags
 * These flags control the visibility of settings sections
 */
export const settingsFeatureFlags = {
  /**
   * Enable Branding section in settings
   * Default: false (hidden)
   * Environment variable: VITE_BRANDING
   */
  branding: isFeatureEnabled('BRANDING'),

  /**
   * Enable API Keys section in settings
   * Default: false (hidden)
   */
  apiKeys: isFeatureEnabled('ENABLE_API_KEYS'),

  /**
   * Enable Webhooks section in settings
   * Default: false (hidden)
   */
  webhooks: isFeatureEnabled('ENABLE_WEBHOOKS'),

  /**
   * Enable Integrations section in settings
   * Default: false (hidden)
   */
  integrations: isFeatureEnabled('ENABLE_INTEGRATIONS'),

  /**
   * Enable Domains section in settings
   * Default: false (hidden)
   */
  domains: isFeatureEnabled('ENABLE_DOMAINS'),

  /**
   * Enable Data Model section in settings
   * Default: false (hidden)
   */
  dataModel: isFeatureEnabled('ENABLE_DATA_MODEL'),
};

/**
 * Log feature flags status (useful for debugging)
 */
export const logFeatureFlags = (): void => {
  console.log('Settings Feature Flags:', {
    branding: settingsFeatureFlags.branding,
    apiKeys: settingsFeatureFlags.apiKeys,
    webhooks: settingsFeatureFlags.webhooks,
    integrations: settingsFeatureFlags.integrations,
    domains: settingsFeatureFlags.domains,
    dataModel: settingsFeatureFlags.dataModel,
  });
};

